// src/features/analytics/components/MediaStreamPage.tsx
//
// Per-stream analytics page `/analytics/media/$mediaId` (dashboard ADR-009;
// gateway routes 7d–7h). The Content tab drills in here; the Drawer remains
// the quick view. Composition: header → window control (Broadcast / Day /
// Range; today by default) → event totals (playback/window) → trend (hourly for short windows,
// daily otherwise) → concurrency → peaks → audience (geo + tech + source) →
// retention (VOD) → heatmap. Everything hour/window-grained needs the gateway's
// `features.hourly`; without it the page says so and keeps the UTC-day panels.

import { Suspense, lazy, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Alert,
  Anchor,
  Badge,
  Group,
  SegmentedControl,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconArrowLeft, IconInfoCircle } from '@tabler/icons-react';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ANALYTICS_GEO_DIMENSIONS,
  ANALYTICS_METRICS,
  ANALYTICS_METRIC_LABELS,
  ANALYTICS_SOURCE_DIMENSIONS,
  ANALYTICS_TECH_DIMENSIONS,
  HOURLY_MAX_RANGE_DAYS,
  type AnalyticsGeoDimension,
  type AnalyticsMediaAudienceDimension,
  type AnalyticsMetric,
} from '@/lib/constants/analytics';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { HEATMAP_MAX_RANGE_DAYS, MAX_RANGE_DAYS, clampRangeSpan, rangeDays } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { DIMENSION_LABELS } from '../audienceValues';
import { broadcastsEmptyCopy, broadcastsLookbackRange, shapeBroadcasts } from '../broadcasts';
import {
  ceilHourSeconds,
  coveringUtcDays,
  floorWindow,
  isTodayWindow,
  isValidEventWindow,
  todayWindow,
  trendGrainFor,
  type EventWindow,
} from '../eventWindow';
import { ANALYTICS_PRESET_LABELS } from '../searchParams';
import { useAnalyticsMediaAudience } from '../useAnalyticsMediaAudience';
import { useAnalyticsMediaPeaks } from '../useAnalyticsMediaPeaks';
import { useAnalyticsMediaRetention } from '../useAnalyticsMediaRetention';
import { useAnalyticsMeta } from '../useAnalyticsMeta';
import { useAnalyticsRange } from '../useAnalyticsRange';
import { useAnalyticsScope } from '../useAnalyticsScope';
import {
  useAnalyticsHeatmapByMedia,
  useAnalyticsMediaBroadcasts,
  useAnalyticsMediaDaily,
  useAnalyticsMediaHourly,
  useAnalyticsMediaWindow,
} from '../useAnalyticsMediaStream';
import { AudienceBreakdownPanel } from './AudienceBreakdownPanel';
import { ConcurrencyHeatmapCard } from './ConcurrencyHeatmapCard';
import { EventTotalsCard } from './EventTotalsCard';
import { EventWindowControl } from './EventWindowControl';
import { MediaConcurrencyCard } from './MediaConcurrencyCard';
import { MediaHourlyTrendCard } from './MediaHourlyTrendCard';
import { MediaPeaksPanel } from './MediaPeaksPanel';
import { MediaRetentionPanel } from './MediaRetentionPanel';
import { PlaybackTrendCard } from './PlaybackTrendCard';

const AudienceMapPanel = lazy(() => import('../map/AudienceMapPanel'));

const AUDIENCE_EMPTY_MESSAGE = 'No audience breakdown for this stream in the selected range.';

/** Section heading with the "(UTC days)" tag the plan requires on UTC-day panels while local days are active. */
function SectionTitle({ children, utcDays }: { children: React.ReactNode; utcDays?: boolean }) {
  const navigate = useNavigate();
  return (
    <Group gap="xs" align="baseline">
      <Title order={4}>{children}</Title>
      {utcDays && (
        <Tooltip
          label="Audience, peaks, retention and the heatmap are finalized per UTC day, so they cover the UTC days that contain your window."
          multiline
          w={320}
          withArrow
        >
          <Anchor
            component="button"
            type="button"
            size="xs"
            c="dimmed"
            underline="always"
            onClick={() => void navigate({ to: '/help/$slug', params: { slug: 'analytics-stream-report' } })}
          >
            (UTC days)
          </Anchor>
        </Tooltip>
      )}
    </Group>
  );
}

export function MediaStreamPage() {
  // The route param is the wire STRING (never coerced to Number).
  const params = useParams({ strict: false }) as { mediaId?: string };
  const mediaId = params.mediaId ?? '';
  const navigate = useNavigate();
  const { features } = useAnalyticsMeta();
  // Local days for the trend/window surfaces; UTC days for audience/peaks/retention/heatmap.
  const local = useAnalyticsRange();
  const { scope } = useAnalyticsScope(local.search.scope);
  // A network Content row drilled through its owning sub-account (mcid); the
  // gateway authorizes parent→child reads. Otherwise the selected customer.
  const cid = local.search.mcid ?? (scope?.kind === 'account' ? scope.cid : null);
  const title = local.search.title ?? mediaId;
  const streamType = local.search.stype;

  // The event window (ADR-009, amended 2026-09-13): the URL's explicit
  // wfrom/wto (a broadcast, a day, or a range) or TODAY in the viewer's zone —
  // never the Content tab's 30-day preset by default. `nowSeconds` is the end
  // of the current hour, so it is stable across renders (query keys do not
  // churn) and a window "to now" includes the hour in progress. The product
  // window floors every window before it is queried; the page says so.
  const nowSeconds = ceilHourSeconds(Math.floor(Date.now() / 1000));
  const explicitWindow: EventWindow | null =
    local.search.wfrom !== undefined && local.search.wto !== undefined
      ? { from: local.search.wfrom, to: local.search.wto }
      : null;
  const requestedWindow = explicitWindow ?? todayWindow(nowSeconds, local.zone);
  const floored = floorWindow(requestedWindow, local.productWindowFloorSeconds);
  const window = floored.window;
  const windowIsCustom = explicitWindow !== null;
  const windowable = isValidEventWindow(window.from, window.to);
  const grain = trendGrainFor(window);
  // Chart-card copy for a window with no viewers (the gateway may answer 422 for a window past the last data hour).
  const windowEmptyCopy = isTodayWindow(window, nowSeconds, local.zone)
    ? 'No viewers yet today.'
    : 'No viewing data in this window.';
  // The Content tab's date range, offered as a secondary window when it fits the hour-grain cap.
  const pageRange: EventWindow | null = isValidEventWindow(local.range.from, local.range.to)
    ? { from: local.range.from, to: local.range.to }
    : null;
  const pageRangeLabel =
    local.search.preset === 'custom'
      ? AnalyticsUtils.formatDayRangeLabel(local.range, local.zone)
      : ANALYTICS_PRESET_LABELS[local.search.preset];

  const canWindow = features.hourly && windowable;
  const windowQuery = useAnalyticsMediaWindow(canWindow, cid, mediaId, window);
  const hourlyQuery = useAnalyticsMediaHourly(canWindow && grain === 'hourly', cid, mediaId, window);
  const dailyQuery = useAnalyticsMediaDaily(
    features.hourly && grain === 'daily',
    cid,
    mediaId,
    clampRangeSpan(window, HOURLY_MAX_RANGE_DAYS),
    local.tz
  );
  // Broadcasts: the lookback of presence, floored to the product window
  // (the gateway refuses earlier starts), independent of the page window.
  const lookback = broadcastsLookbackRange(nowSeconds, local.productWindowFloorSeconds);
  const broadcastsQuery = useAnalyticsMediaBroadcasts(
    features.hourly && streamType !== 'vod',
    cid,
    mediaId,
    { from: lookback.from, to: lookback.to }
  );
  const broadcastsEmpty = broadcastsEmptyCopy(lookback.floored, local.productWindowFloorSeconds, local.zone);
  // Presence runs → broadcasts: re-join lulls, drop noise, recognize an always-on channel.
  const shapedBroadcasts = useMemo(
    () => shapeBroadcasts(broadcastsQuery.data?.data.rows ?? [], lookback),
    [broadcastsQuery.data, lookback.from, lookback.to] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // UTC-day panels (audience, peaks, retention) follow the page's UTC-day range.
  const [dimension, setDimension] = useState<AnalyticsMediaAudienceDimension>('country');
  const [metric, setMetric] = useState<AnalyticsMetric>('watchTime');
  const [view, setView] = useState<'list' | 'map'>('list');
  const isGeoDimension = (ANALYTICS_GEO_DIMENSIONS as readonly string[]).includes(dimension);
  const effectiveView = isGeoDimension && view === 'map' ? 'map' : 'list';
  // One window drives the page (ADR-009 amendment): the UTC-day panels cover
  // the UTC days that contain the window — no separate date range up top.
  const utcDays = clampRangeSpan(coveringUtcDays(window), MAX_RANGE_DAYS);
  const audienceQuery = useAnalyticsMediaAudience(cid, mediaId, dimension, utcDays, metric);
  const peaksQuery = useAnalyticsMediaPeaks(cid, mediaId, utcDays);
  const retentionQuery = useAnalyticsMediaRetention(cid, streamType === 'vod' ? mediaId : null, utcDays);
  const heatmapRangeWasClamped = rangeDays(utcDays) > HEATMAP_MAX_RANGE_DAYS;
  const heatmapRange = clampRangeSpan(utcDays, HEATMAP_MAX_RANGE_DAYS);
  const heatmapQuery = useAnalyticsHeatmapByMedia(cid, mediaId, heatmapRange);

  const showExpansion = FEATURE_FLAGS.analyticsExpansionEnabled;
  const rangeLabel = AnalyticsUtils.formatDayRangeLabel(utcDays, 'utc');

  const setWindow = (next: EventWindow | null) => {
    void navigate({
      to: '.',
      search: (prev) => ({
        ...(prev as Record<string, unknown>),
        wfrom: next?.from,
        wto: next?.to,
      }),
    });
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Stack gap={4}>
          <Anchor
            component="button"
            type="button"
            size="sm"
            onClick={() => void navigate({ to: '/analytics/content', search: true })}
          >
            <Group gap={4} component="span">
              <IconArrowLeft size={14} /> Content
            </Group>
          </Anchor>
          <Group gap="sm" align="center">
            {/* Title decoration fallback is the raw mediaId STRING (support correlation id). */}
            <Title order={3}>{title}</Title>
            {streamType && (
              <Badge size="sm" variant="light" color={streamType === 'live' ? 'blue' : 'gray'}>
                {streamType === 'live' ? 'Live' : 'VOD'}
              </Badge>
            )}
          </Group>
          {title !== mediaId && (
            <Text size="xs" c="dimmed">
              Media ID {mediaId}
            </Text>
          )}
        </Stack>
      </Group>

      {!features.hourly && (
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>
          Event windows and hourly trends need the latest analytics service. Until it is
          enabled, this page shows the daily audience, peaks, and retention panels only.
        </Alert>
      )}

      {features.hourly && (
        <EventWindowControl
          value={window}
          explicit={windowIsCustom}
          onChange={setWindow}
          zone={local.zone}
          nowSeconds={nowSeconds}
          floorSeconds={local.productWindowFloorSeconds}
          pageRange={pageRange}
          pageRangeLabel={pageRangeLabel}
          showBroadcasts={streamType !== 'vod'}
          broadcasts={{
            rows: shapedBroadcasts.rows,
            isLoading: broadcastsQuery.isLoading,
            isError: broadcastsQuery.isError,
            onRetry: broadcastsQuery.refetch,
            emptyCopy: broadcastsEmpty,
            continuous: shapedBroadcasts.continuous,
          }}
        />
      )}

      {features.hourly && floored.clamped && local.productWindowFloorSeconds !== null && (
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>
          New Analytics starts{' '}
          {DateTime.fromSeconds(local.productWindowFloorSeconds, { zone: local.zone === 'utc' ? 'utc' : local.zone }).toFormat('MMM d, yyyy')}
          . The window begins there.
        </Alert>
      )}

      {features.hourly && !windowable && (
        <Alert color="gray" icon={<IconInfoCircle size={16} />}>
          Event totals cover windows of up to {HOURLY_MAX_RANGE_DAYS} days. Pick a shorter range or
          a broadcast to see hour-aligned totals and the peak.
        </Alert>
      )}

      {features.hourly && windowable && (
        <EventTotalsCard
          requested={window}
          isCustom={windowIsCustom}
          title={windowIsCustom ? 'Event totals' : 'Today so far'}
          zone={local.zone}
          nowSeconds={nowSeconds}
          broadcasts={shapedBroadcasts.rows}
          onPickWindow={setWindow}
          data={windowQuery.data}
          isLoading={windowQuery.isLoading}
          isError={windowQuery.isError}
          error={windowQuery.error}
          onRetry={windowQuery.refetch}
        />
      )}

      {features.hourly &&
        (grain === 'hourly' && windowable ? (
          <MediaHourlyTrendCard
            hourly={hourlyQuery.data}
            window={window}
            isLoading={hourlyQuery.isLoading}
            isError={hourlyQuery.isError}
            error={hourlyQuery.error}
            onRetry={hourlyQuery.refetch}
            emptyCopy={windowEmptyCopy}
            zone={local.zone}
          />
        ) : (
          <PlaybackTrendCard
            title="Views over time"
            playbackDaily={dailyQuery.data}
            range={clampRangeSpan(window, HOURLY_MAX_RANGE_DAYS)}
            rangeTotals={null}
            isLoading={dailyQuery.isLoading}
            isError={dailyQuery.isError}
            error={dailyQuery.error}
            onRetry={dailyQuery.refetch}
            zone={local.zone}
            emptyCopy={windowEmptyCopy}
          />
        ))}

      {features.hourly && (
        <MediaConcurrencyCard cid={cid} mediaId={mediaId} window={window} emptyCopy={windowEmptyCopy} zone={local.zone} />
      )}

      {showExpansion && (
        <Stack gap="xs">
          <SectionTitle utcDays={local.localDays}>Peaks</SectionTitle>
          <MediaPeaksPanel
            peaks={peaksQuery.peaks}
            range={utcDays}
            isLoading={peaksQuery.isLoading}
            isError={peaksQuery.isError}
            error={peaksQuery.error}
            onRetry={peaksQuery.refetch}
          />
        </Stack>
      )}

      <Stack gap="xs">
        <SectionTitle utcDays={local.localDays}>Audience</SectionTitle>
        <Group gap="sm" wrap="wrap">
          <SegmentedControl
            size="xs"
            data={ANALYTICS_GEO_DIMENSIONS.map((d) => ({ value: d, label: DIMENSION_LABELS[d] }))}
            value={isGeoDimension ? dimension : ''}
            onChange={(value) => value && setDimension(value as AnalyticsMediaAudienceDimension)}
          />
          <SegmentedControl
            size="xs"
            data={ANALYTICS_TECH_DIMENSIONS.map((d) => ({ value: d, label: DIMENSION_LABELS[d] }))}
            value={(ANALYTICS_TECH_DIMENSIONS as readonly string[]).includes(dimension) ? dimension : ''}
            onChange={(value) => value && setDimension(value as AnalyticsMediaAudienceDimension)}
            disabled={!features.hourly}
          />
          {showExpansion && (
            <SegmentedControl
              size="xs"
              data={ANALYTICS_SOURCE_DIMENSIONS.map((d) => ({ value: d, label: DIMENSION_LABELS[d] }))}
              value={(ANALYTICS_SOURCE_DIMENSIONS as readonly string[]).includes(dimension) ? dimension : ''}
              onChange={(value) => value && setDimension(value as AnalyticsMediaAudienceDimension)}
            />
          )}
          <SegmentedControl
            size="xs"
            data={ANALYTICS_METRICS.map((m) => ({ value: m, label: ANALYTICS_METRIC_LABELS[m] }))}
            value={metric}
            onChange={(value) => setMetric(value as AnalyticsMetric)}
          />
          {isGeoDimension && (
            <SegmentedControl
              size="xs"
              data={[
                { value: 'list', label: 'List' },
                { value: 'map', label: 'Map' },
              ]}
              value={effectiveView}
              onChange={(value) => setView(value as 'list' | 'map')}
            />
          )}
        </Group>
        {effectiveView === 'map' ? (
          <Suspense fallback={<Skeleton height={380} />}>
            <AudienceMapPanel
              dimension={dimension as AnalyticsGeoDimension}
              metric={metric}
              audience={audienceQuery.audience}
              range={utcDays}
              isLoading={audienceQuery.isLoading}
              isError={audienceQuery.isError}
              error={audienceQuery.error}
              onRetry={audienceQuery.refetch}
              emptyMessage={AUDIENCE_EMPTY_MESSAGE}
              rangeLabel={rangeLabel}
            />
          </Suspense>
        ) : (
          <AudienceBreakdownPanel
            dimension={dimension}
            metric={metric}
            audience={audienceQuery.audience}
            range={utcDays}
            isLoading={audienceQuery.isLoading}
            isError={audienceQuery.isError}
            error={audienceQuery.error}
            onRetry={audienceQuery.refetch}
            emptyMessage={AUDIENCE_EMPTY_MESSAGE}
          />
        )}
      </Stack>

      {showExpansion && streamType === 'vod' && (
        <MediaRetentionPanel
          retention={retentionQuery.retention}
          range={utcDays}
          isLoading={retentionQuery.isLoading}
          isError={retentionQuery.isError}
          error={retentionQuery.error}
          onRetry={retentionQuery.refetch}
        />
      )}

      <SimpleGrid cols={1}>
        <ConcurrencyHeatmapCard
          heatmap={heatmapQuery.data}
          range={heatmapRange}
          isLoading={heatmapQuery.isLoading}
          isError={heatmapQuery.isError}
          error={heatmapQuery.error}
          onRetry={heatmapQuery.refetch}
          rangeWasClamped={heatmapRangeWasClamped}
        />
      </SimpleGrid>
    </Stack>
  );
}
